import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ShieldAlert, Beaker, Globe, AlertTriangle, Layers } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SectionTitle } from "@/components/Bits";

// Mode-driven copy + execution-context labels. Same pattern as
// Dashboard.jsx::MODE_VIEW so every mode-sensitive page derives its
// labels from a single table per file.
const MODE_VIEW = {
  paper: {
    label: "PAPER",
    tone: "neutral",
    appliesCopy: "Applied to all simulated paper orders.",
  },
  testnet: {
    label: "TESTNET",
    tone: "amber",
    appliesCopy: "Applied to Binance Testnet execution.",
  },
  live_disabled: {
    label: "EXECUTION DISABLED",
    tone: "amber",
    appliesCopy: "Saved here but the engine is currently NOT routing orders (mode = Live Disabled).",
  },
  live_enabled: {
    label: "BINANCE LIVE",
    tone: "live",
    appliesCopy: "Applied to live Binance execution. Real orders, real funds.",
  },
  lbank_live_enabled: {
    label: "LBANK LIVE",
    tone: "live",
    appliesCopy: "Applied to live LBank execution. Real orders, real funds.",
  },
};

// Settings that the backend (`risk_manager.check_can_open` +
// `signal_engine`) enforces UNCONDITIONALLY across every bot_mode.
const UNIVERSAL_FIELDS = [
  ["max_trades_per_day", "Max trades per day"],
  ["max_daily_loss_usdt", "Max daily loss (USDT)"],
  ["cooldown_after_loss_minutes", "Cooldown after loss (min)"],
  ["min_time_between_trades_seconds", "Min time between trades (s)"],
  ["min_price_move_between_same_side_entries_pct", "Min price move between same-side entries (%)"],
];

// Stored but NOT currently enforced by the backend — flagged
// transparently so operators don't think they have a daily-percent
// circuit breaker that doesn't actually exist.
const NOT_ENFORCED_FIELDS = [
  ["max_daily_loss_percent", "Max daily loss (%) — stored, not enforced today"],
];

// Per-side open-position caps. Backend currently scopes the SCOPE
// query to `exchange=binance_testnet` (signal_engine.py:868 +
// validation_phase.py:152). When live execution is enabled these
// caps will not actually gate live entries until the SCOPE query is
// generalised — that's a backend follow-up, NOT part of this UI PR.
const PER_SIDE_FIELDS = [
  ["max_open_long_positions", "Max open LONG positions"],
  ["max_open_short_positions", "Max open SHORT positions"],
];

// Testnet-only — backend gates these with `if bot_mode == "testnet":`
// in `risk_manager.check_can_open`. They are inert in every other mode.
const TESTNET_FIELDS = [
  ["testnet_max_leverage", "Max leverage (x)"],
  ["testnet_max_position_btc", "Max position size (BTC)"],
  ["testnet_max_concurrent_positions", "Max concurrent positions"],
];

export default function RiskSettings() {
  const [r, setR] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    (async () => {
      const [risk, st] = await Promise.all([
        api.getRisk(),
        api.status().catch(() => null),
      ]);
      setR(risk);
      setStatus(st);
    })();
    // Re-fetch when BotSettings saves a new bot_mode so the
    // execution-context indicator and copy lines update instantly.
    const onSaved = async () => {
      try { setStatus(await api.status()); } catch { /* keep last */ }
    };
    window.addEventListener("bot-settings:saved", onSaved);
    return () => window.removeEventListener("bot-settings:saved", onSaved);
  }, []);

  const save = async () => {
    try { setR(await api.putRisk(r)); toast.success("Risk settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const botMode = status?.bot_settings?.bot_mode || "paper";
  const view = MODE_VIEW[botMode] || MODE_VIEW.paper;
  const isTestnet = botMode === "testnet";
  const isLive = botMode === "live_enabled" || botMode === "lbank_live_enabled";
  const engineRunning = status?.runtime?.state === "running";

  // Visual class helpers — colour scheme matches the rest of the app:
  // green (running), red (live execution), amber (testnet/paper), gray
  // (stopped). Keeping these inline avoids reaching for a global theme
  // utility just for this page.
  const toneBg = useMemo(() => {
    if (view.tone === "live") return "bg-[#FF3B30]/10 border-[#FF3B30]/40 text-[#FF3B30]";
    if (view.tone === "amber") return "bg-[#F59E0B]/10 border-[#F59E0B]/40 text-[#F59E0B]";
    return "bg-[#1A1A22] border-border text-[#A0A0AB]";
  }, [view.tone]);

  if (!r) return <div className="p-6 text-sm text-[#A0A0AB]">Loading…</div>;

  const renderField = ([key, label], extraProps = {}) => (
    <div key={key} className="space-y-2">
      <Label>{label}</Label>
      <Input
        data-testid={`risk-${key}`}
        type="number"
        value={r[key] ?? 0}
        onChange={(e) => setR({ ...r, [key]: parseFloat(e.target.value || "0") })}
        className="bg-[#1A1A22] border-border rounded-sm font-mono disabled:opacity-40"
        {...extraProps}
      />
    </div>
  );

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <SectionTitle sub={view.appliesCopy}>Risk Settings</SectionTitle>

      {/* Execution-context indicator. Always at the top of the page so
          the operator can never misread which mode the saved settings
          will gate. Includes engine state so a stopped-engine save is
          obvious too. */}
      <div
        data-testid="risk-execution-context"
        data-mode={botMode}
        data-engine-state={status?.runtime?.state || "unknown"}
        className={`rounded-sm border px-4 py-3 flex items-center gap-3 ${toneBg}`}
      >
        <ShieldAlert size={16} strokeWidth={2} className="shrink-0" />
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-y-1 gap-x-6 text-xs uppercase tracking-wider">
          <div>
            Execution Mode:{" "}
            <span className="font-semibold" data-testid="risk-context-mode">
              {view.label}
            </span>
          </div>
          <div>
            Engine State:{" "}
            <span
              className={`font-semibold ${engineRunning ? "text-[#00E676]" : "text-[#71717A]"}`}
              data-testid="risk-context-engine"
            >
              {engineRunning ? "RUNNING" : (status?.runtime?.state || "STOPPED").toUpperCase()}
            </span>
          </div>
        </div>
      </div>

      {/* ====== SECTION A: Universal protections ====== */}
      <div className="bg-[#0F0F14] border border-border rounded-sm" data-testid="risk-universal-card">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Globe size={14} strokeWidth={1.8} className="text-[#A0A0AB]" />
            <span className="text-xs uppercase tracking-widest text-[#A0A0AB]">Universal protections</span>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-[#A0A0AB] border border-border bg-[#1A1A22] px-2 py-0.5 rounded-sm">
            ALL MODES
          </span>
        </div>
        <div className="p-5 space-y-5">
          <div className="text-xs text-[#71717A]">
            These fire on every entry regardless of bot mode — paper, testnet, Binance Live, LBank Live.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {UNIVERSAL_FIELDS.map((f) => renderField(f))}
          </div>

          {/* Kill switch */}
          <div className="flex items-center justify-between border-t border-border pt-4">
            <div>
              <div className="text-sm font-medium short-text">Kill Switch</div>
              <div className="text-xs text-[#A0A0AB]">
                When enabled, blocks <span className="font-semibold">all</span> new entries (paper, testnet, live) until disabled.
              </div>
            </div>
            <Switch
              data-testid="kill-switch-toggle"
              checked={r.kill_switch_enabled}
              onCheckedChange={(v) => setR({ ...r, kill_switch_enabled: v })}
            />
          </div>

          {/* Martingale */}
          <div className="flex items-center justify-between border-t border-border pt-4">
            <div>
              <div className="text-sm font-medium short-text">Enable Martingale</div>
              <div className="text-xs text-[#A0A0AB]">
                Opt-in. OFF by default. Each same-side repeat entry scales the base notional by{" "}
                <span className="font-mono">multiplier^count</span>. Leverage never changes. Strategy must still produce a new valid signal for each entry.
              </div>
            </div>
            <Switch
              data-testid="martingale-enabled-toggle"
              checked={!!r.martingale_enabled}
              onCheckedChange={(v) => setR({ ...r, martingale_enabled: v })}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label>Martingale multiplier</Label>
              <Input
                data-testid="risk-martingale_multiplier"
                type="number" step="0.1" min="1"
                value={r.martingale_multiplier ?? 2}
                onChange={(e) => setR({ ...r, martingale_multiplier: Math.max(1, parseFloat(e.target.value || "1")) })}
                disabled={!r.martingale_enabled}
                className="bg-[#1A1A22] border-border rounded-sm font-mono disabled:opacity-40"
              />
            </div>
            <div className="space-y-2">
              <Label>Martingale max notional cap (USDT)</Label>
              <Input
                data-testid="risk-martingale_max_notional"
                type="number" min="0" step="1"
                value={r.martingale_max_notional ?? 0}
                onChange={(e) => setR({ ...r, martingale_max_notional: Math.max(0, parseFloat(e.target.value || "0")) })}
                disabled={!r.martingale_enabled}
                placeholder="0 = no cap"
                className="bg-[#1A1A22] border-border rounded-sm font-mono disabled:opacity-40"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ====== SECTION B: Per-side caps (currently testnet-scoped in backend) ====== */}
      <div className="bg-[#0F0F14] border border-border rounded-sm" data-testid="risk-per-side-card">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers size={14} strokeWidth={1.8} className="text-[#A0A0AB]" />
            <span className="text-xs uppercase tracking-widest text-[#A0A0AB]">Per-side open-position caps</span>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-[#F59E0B] border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-2 py-0.5 rounded-sm">
            TESTNET-SCOPED IN BACKEND
          </span>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-[#F59E0B] flex items-start gap-2">
            <AlertTriangle size={12} strokeWidth={2} className="mt-0.5 shrink-0" />
            <span>
              These caps are currently enforced ONLY when{" "}
              <span className="font-mono">bot_mode=testnet</span>. The backend SCOPE query is hardcoded to{" "}
              <span className="font-mono">exchange=binance_testnet</span>. Until that is generalised, the
              values you save here will <span className="font-semibold">NOT</span> gate live Binance / LBank entries.
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PER_SIDE_FIELDS.map((f) => renderField(f))}
          </div>
        </div>
      </div>

      {/* ====== SECTION C: Testnet-only caps ======
          Visible in every mode so the operator can pre-tune values
          before switching to testnet, but visually de-emphasised when
          the active mode isn't testnet. The TESTNET badge stays only
          to label the section — it does NOT imply the engine is in
          testnet (the top context indicator is the source of truth). */}
      <div
        className={`bg-[#0F0F14] border border-border rounded-sm transition-opacity ${
          isTestnet ? "" : "opacity-60"
        }`}
        data-testid="risk-testnet-caps-card"
        data-active={isTestnet}
      >
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Beaker size={14} strokeWidth={1.8} className="text-[#A0A0AB]" />
            <span className="text-xs uppercase tracking-widest text-[#A0A0AB]">Testnet execution caps</span>
          </div>
          <span
            className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${
              isTestnet
                ? "text-[#F59E0B] border-[#F59E0B]/40 bg-[#F59E0B]/10"
                : "text-[#71717A] border-border bg-[#1A1A22]"
            }`}
            data-testid="risk-testnet-badge"
          >
            {isTestnet ? "ACTIVE · TESTNET" : "INACTIVE IN " + view.label}
          </span>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-[#A0A0AB]">
            {isTestnet
              ? "Hard limits enforced by the risk manager when the engine routes to Binance Testnet."
              : `These limits are inactive while the engine is in ${view.label}. They will become active again only if you switch bot_mode back to TESTNET in Bot Settings.`}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {TESTNET_FIELDS.map(([key, label]) =>
              renderField([key, label], { step: key === "testnet_max_position_btc" ? "0.0001" : "1" })
            )}
            <div className="space-y-2">
              <Label>Allowed symbols (comma-separated)</Label>
              <Input
                data-testid="risk-testnet_allowed_symbols"
                value={(r.testnet_allowed_symbols || []).join(",")}
                onChange={(e) =>
                  setR({
                    ...r,
                    testnet_allowed_symbols: e.target.value
                      .split(",")
                      .map((s) => s.trim().toUpperCase())
                      .filter(Boolean),
                  })
                }
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ====== SECTION D: Stored but not currently enforced ======
          Keeps these visible so operators don't lose their saved values
          on mode switches, but explicitly labels them as inert so no
          one believes they have a daily-percent circuit breaker that
          doesn't actually exist in the backend. */}
      <div className="bg-[#0F0F14] border border-border rounded-sm opacity-70" data-testid="risk-not-enforced-card">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} strokeWidth={1.8} className="text-[#71717A]" />
            <span className="text-xs uppercase tracking-widest text-[#71717A]">Stored — not enforced today</span>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-[#71717A] border border-border bg-[#1A1A22] px-2 py-0.5 rounded-sm">
            INERT
          </span>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-[#71717A]">
            These fields are persisted in the bot config but the risk manager does NOT yet read them. Edit them at your discretion — they will not gate any orders until the backend wires them in.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {NOT_ENFORCED_FIELDS.map((f) => renderField(f, { step: "0.01" }))}
          </div>
        </div>
      </div>

      <Button
        data-testid="save-risk-btn"
        onClick={save}
        className={`rounded-sm ${
          isLive
            ? "bg-[#FF3B30] text-white hover:bg-[#E5352B]"
            : "bg-white text-black hover:bg-[#A0A0AB]"
        }`}
      >
        {isLive ? "Save risk settings (LIVE)" : "Save risk settings"}
      </Button>
    </div>
  );
}
